<script setup>
  import {
    ref,
    computed,
    watch,
    onMounted,
    onUnmounted,
    getCurrentInstance,
  } from 'vue';
  import axios from 'axios';
  import BaseModal from '@/shared/components/modals/BaseModal.vue';
  import BaseButton from '@/shared/components/base/BaseButton.vue';
  import FloorPlanRenderer from '@/apps/admin/components/floorplan/FloorPlanRenderer.vue';
  import FloorPlanService from '@/services/FloorPlanService';
  import { SeatSelectionService } from '@/services/SeatSelectionService';
  import { cartV2Store } from '@/store/cartV2';
  import { useBreakpoint } from '@/shared/composables/useBreakpoint';
  import {
    buildSeatIntentPayload,
    getSeatDisplayLabel,
    publishSeatIntent as publishSeatIntentMessage,
  } from '@/utils/seatSelectionRealtime';

  // Get i18n from global properties (legacy mode)
  const instance = getCurrentInstance();
  const t = (key) => instance.proxy.$t(key);
  const cartStore = cartV2Store();

  // Breakpoint detection
  const { isMobile } = useBreakpoint();

  const props = defineProps({
    open: {
      type: Boolean,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
    channel: {
      type: Object,
      required: true,
    },
    sessionUserId: {
      type: [String, Number],
      required: true,
    },
    existingCartTickets: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits([
    'close',
    'seats-selected',
    'add-tickets-to-cart',
    'back-to-overview',
  ]);

  // State
  const loading = ref(false);
  const loadingEventDetail = ref(false);
  const floorPlanConfig = ref(null);
  const selectedSeats = ref(new Set());
  const reservedSeatIds = ref(new Set());
  const myReservedSeats = ref(new Set());
  const mobileStep = ref('floorplan'); // 'floorplan' or 'selection'
  const eventDetail = ref(null);
  const ticketTypesData = ref([]);

  // Computed
  const ticketTypes = computed(() => {
    // Use ticket types from event detail API
    const tickets = ticketTypesData.value || [];
    return tickets.map((ticket) => ({
      ...ticket,
      seats: Array.isArray(ticket.seats)
        ? ticket.seats.map((seat) =>
            typeof seat === 'string' ? { id: seat } : seat
          )
        : [],
    }));
  });

  // Event display data - use eventDetail if available, fallback to props.event
  const eventDisplayName = computed(() => {
    return (
      eventDetail.value?.name || props.event?.name || props.event?.title || ''
    );
  });

  const eventDisplayDate = computed(() => {
    if (!eventDetail.value?.date) return props.event?.date || '';

    const date = new Date(eventDetail.value.date);
    return date.toLocaleDateString('nl-NL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  });

  const eventDisplayStartTime = computed(() => {
    if (!eventDetail.value?.startTime) return props.event?.start_time || '';

    const time = new Date(eventDetail.value.startTime);
    return time.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  });

  const eventDisplayEndTime = computed(() => {
    if (!eventDetail.value?.endTime) return props.event?.end_time || '';

    const time = new Date(eventDetail.value.endTime);
    return time.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  });

  const eventDisplayLocation = computed(() => {
    return (
      eventDetail.value?.location ||
      props.event?.location?.name ||
      props.event?.location ||
      ''
    );
  });

  const selectedSeatsList = computed(() => {
    return Array.from(selectedSeats.value).map((seatId) => {
      // Find the seat in the floor plan config
      const seat = findSeatById(seatId);
      // Find the ticket type for this seat
      const ticketTypeId = seatTicketMappings.value[seatId];
      const ticketType = ticketTypes.value.find((t) => t.id === ticketTypeId);

      // Generate visual label based on seat type
      let label = seatId; // Fallback
      if (seat) {
        // Check if this is a table seat (has tableNumber in metadata)
        if (seat.metadata?.tableNumber && seat.metadata?.seatPosition) {
          label = `Tafel ${seat.metadata.tableNumber} - Stoel ${seat.metadata.seatPosition}`;
        } else if (seat.row && seat.number) {
          // Theater/Cinema seat
          label = `${seat.row}-${seat.number}`;
        }
      }

      return {
        id: seatId,
        label: label,
        ticketType: ticketType,
        price: ticketType?.price || 0,
      };
    });
  });

  const totalPrice = computed(() => {
    return selectedSeatsList.value.reduce((sum, seat) => sum + seat.price, 0);
  });

  // Find the default ticket type (the one without specific seats assigned, or the first one)
  const defaultTicketType = computed(() => {
    // Find ticket type with no seats or empty seats array
    const defaultType = ticketTypes.value.find(
      (type) =>
        !type.seats || !Array.isArray(type.seats) || type.seats.length === 0
    );
    // If no default found, use the first ticket type
    return defaultType || ticketTypes.value[0];
  });

  const seatTicketMappings = computed(() => {
    const mappings = {};

    // First, assign specific ticket types to their seats
    ticketTypes.value.forEach((type) => {
      if (type.seats && Array.isArray(type.seats) && type.seats.length > 0) {
        type.seats.forEach((seat) => {
          const seatId = typeof seat === 'object' && seat.id ? seat.id : seat;
          if (seatId) {
            mappings[seatId] = type.id;
          }
        });
      }
    });

    // Then, assign default ticket type to all seats that don't have a specific type
    if (floorPlanConfig.value && defaultTicketType.value) {
      const allSeats = getAllSeatsFromFloorPlan();
      allSeats.forEach((seatId) => {
        if (!mappings[seatId]) {
          mappings[seatId] = defaultTicketType.value.id;
        }
      });
    }

    return mappings;
  });

  const visibleTicketTypes = computed(() => {
    // Show all ticket types (including default)
    return ticketTypes.value;
  });

  // Computed property to ensure reactivity for reserved seats
  const reservedSeatsSet = computed(() => {
    // Create a new Set to ensure Vue detects changes
    return new Set(reservedSeatIds.value);
  });

  // Helper function to get all seat IDs from floor plan
  function getAllSeatsFromFloorPlan() {
    if (!floorPlanConfig.value) return [];

    try {
      const config =
        typeof floorPlanConfig.value === 'string'
          ? JSON.parse(floorPlanConfig.value)
          : floorPlanConfig.value;

      const seats = config.elements?.filter((el) => el.type === 'seat') || [];
      return seats.map((seat) => seat.id);
    } catch (error) {
      console.error('Error parsing floor plan config:', error);
      return [];
    }
  }

  // Helper function to darken color for border
  function darkenColorForBorder(color) {
    if (!color) return '#D1D5DB';

    // Simple darkening by reducing RGB values by 40%
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const darkenedR = Math.floor(r * 0.6);
    const darkenedG = Math.floor(g * 0.6);
    const darkenedB = Math.floor(b * 0.6);

    return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG
      .toString(16)
      .padStart(2, '0')}${darkenedB.toString(16).padStart(2, '0')}`;
  }

  // Methods
  async function loadEventDetail() {
    if (!props.event?.id) return;

    loadingEventDetail.value = true;
    try {
      const response = await SeatSelectionService.getEventForSeatSelection(
        props.event.id
      );
      eventDetail.value = response.data;
      ticketTypesData.value = response.data.ticketTypes || [];

      console.log('Loaded event detail for seat selection:', eventDetail.value);
      console.log('Ticket types:', ticketTypesData.value);
    } catch (error) {
      console.error('Failed to load event detail:', error);
    } finally {
      loadingEventDetail.value = false;
    }
  }

  function findSeatById(seatId) {
    if (!floorPlanConfig.value?.elements) return null;
    return floorPlanConfig.value.elements.find(
      (el) => el.id === seatId && el.type === 'seat'
    );
  }

  function getTicketTypeForSeat(seat) {
    // Check if seat has a specific ticket type assigned via seatTicketMappings
    const ticketTypeId = seatTicketMappings.value[seat.id];

    if (ticketTypeId) {
      // Find the ticket type by ID
      return ticketTypes.value.find((tt) => tt.id === ticketTypeId);
    }

    // If no specific mapping, use the default ticket type (isDefault: true)
    return ticketTypes.value.find((tt) => tt.isDefault);
  }

  async function loadFloorPlan() {
    if (!props.event.floorPlan) return;

    loading.value = true;
    try {
      const response = await FloorPlanService.getPublic(props.event.floorPlan);
      floorPlanConfig.value = response.config;

      // After loading floor plan, fetch current seat reservations
      await fetchReservedSeats();

      // Pre-select existing cart tickets
      preselectExistingCartTickets();
    } catch (error) {
      console.error('Failed to load floor plan:', error);
    } finally {
      loading.value = false;
    }
  }

  function preselectExistingCartTickets() {
    if (!props.existingCartTickets || props.existingCartTickets.length === 0)
      return;
    if (!floorPlanConfig.value?.elements) return;

    // Convert cart ticket labels to seat IDs and select them
    props.existingCartTickets.forEach((cartTicket) => {
      // Support both V1 (ticket.label / label) and V2 (metadata.seat_label) cart formats
      const label =
        cartTicket.ticket?.label ||
        cartTicket.label ||
        cartTicket.metadata?.seat_label;
      if (!label) return;

      // Find the seat element with this label
      const seatElement = floorPlanConfig.value.elements.find((el) => {
        if (el.type !== 'seat') return false;
        const constructedLabel = `${el.row}-${el.number}`;
        return constructedLabel === label;
      });

      if (seatElement) {
        selectedSeats.value.add(seatElement.id);
        myReservedSeats.value.add(seatElement.id);
        // Don't add to reservedSeatIds - these are our selected seats
      }
    });

    // Trigger reactivity
    selectedSeats.value = new Set(selectedSeats.value);
    myReservedSeats.value = new Set(myReservedSeats.value);
  }

  async function fetchReservedSeats() {
    try {
      const response = await axios.get(
        `/api/ticket/reserved/${props.event.id}`
      );
      const ticketsData = response.data;

      // Convert object to array if needed (API returns object with numeric keys)
      const tickets = Array.isArray(ticketsData)
        ? ticketsData
        : Object.values(ticketsData);

      // Build a map of seat IDs that are reserved by others or sold
      // Seats in our own cart should be in selectedSeats, not reservedSeatIds
      const reserved = new Set();
      const myReserved = new Set();
      const mySelected = new Set();

      if (floorPlanConfig.value?.elements) {
        tickets.forEach((ticket) => {
          // Try to get seat ID from seatId field (V2 system)
          let seatId = ticket.seatId;

          // If no seatId, try to find by label (old system compatibility)
          if (!seatId) {
            const seatLabel = ticket.label || ticket.code;

            if (seatLabel) {
              // Find the seat element with this label
              // Label format is "ROW-NUMBER" (e.g., "I-9")
              const seatElement = floorPlanConfig.value.elements.find((el) => {
                if (el.type !== 'seat') return false;

                // Construct label from row and number
                const constructedLabel = `${el.row}-${el.number}`;
                return constructedLabel === seatLabel;
              });

              if (seatElement) {
                seatId = seatElement.id;
              }
            }
          }

          if (seatId) {
            // Check if this is the current user's reservation
            // For OrderTickets: check user.id
            // For cart items: use isOwnReservation flag from backend
            const isOwn =
              ticket.isOwnReservation ||
              ticket.user?.id === props.sessionUserId;

            if (isOwn) {
              // This is our own reservation
              myReserved.add(seatId);

              // If it's in our cart (not yet paid), add to selected seats
              // Cart items have isCartReservation: true
              if (ticket.isCartReservation) {
                mySelected.add(seatId);
                // DON'T add to reserved - this is a selected seat
              } else {
                // Paid tickets - show as reserved (not selectable)
                reserved.add(seatId);
              }
            } else {
              // Reserved by someone else or sold
              reserved.add(seatId);
            }
          }
        });
      }

      reservedSeatIds.value = reserved;
      myReservedSeats.value = myReserved;

      // Add cart items to selected seats
      mySelected.forEach((seatId) => {
        selectedSeats.value.add(seatId);
      });
      selectedSeats.value = new Set(selectedSeats.value);
    } catch (error) {
      console.error('Failed to fetch reserved seats:', error);
    }
  }

  // Debounce mechanism to prevent double-clicks
  const clickTimestamps = new Map();
  const CLICK_DEBOUNCE_MS = 500; // 500ms debounce

  async function handleSeatClick(seatId) {
    const now = Date.now();
    const lastClick = clickTimestamps.get(seatId);

    if (lastClick && now - lastClick < CLICK_DEBOUNCE_MS) {
      console.warn(
        '⚠️ Ignoring double-click on seat:',
        seatId,
        'time since last click:',
        now - lastClick,
        'ms'
      );
      return;
    }

    clickTimestamps.set(seatId, now);
    console.log('🔵 handleSeatClick called:', seatId, new Date().toISOString());

    // Check if seat is reserved by someone else
    if (
      reservedSeatIds.value.has(seatId) &&
      !myReservedSeats.value.has(seatId)
    ) {
      console.log('❌ Seat reserved by someone else, ignoring click');
      return;
    }

    const seat = findSeatById(seatId);
    if (!seat) {
      console.log('❌ Seat not found, ignoring click');
      return;
    }

    // Toggle selection - add or remove from cart immediately
    const isSelected = selectedSeats.value.has(seatId);
    console.log('🔵 Seat selection state:', {
      seatId,
      isSelected,
      action: isSelected ? 'REMOVE' : 'ADD',
    });

    if (isSelected) {
      console.log('🔴 REMOVING seat from cart:', seatId);
      // OPTIMISTIC UPDATE: Remove immediately for instant feedback
      selectedSeats.value.delete(seatId);
      myReservedSeats.value.delete(seatId);
      reservedSeatIds.value.delete(seatId);
      selectedSeats.value = new Set(selectedSeats.value);
      myReservedSeats.value = new Set(myReservedSeats.value);
      reservedSeatIds.value = new Set(reservedSeatIds.value);

      // Publish optimistic event for other clients
      publishSeatIntent(seat, 'released');

      // API call to backend
      const success = await removeSeatFromCart(seat);

      // Rollback on error
      if (!success) {
        selectedSeats.value.add(seatId);
        myReservedSeats.value.add(seatId);
        reservedSeatIds.value.add(seatId);
        selectedSeats.value = new Set(selectedSeats.value);
        myReservedSeats.value = new Set(myReservedSeats.value);
        reservedSeatIds.value = new Set(reservedSeatIds.value);
      }
    } else {
      console.log('🟢 ADDING seat to cart:', seatId);

      // OPTIMISTIC UPDATE: Add immediately for instant feedback
      selectedSeats.value.add(seatId);
      myReservedSeats.value.add(seatId);
      reservedSeatIds.value.add(seatId);
      selectedSeats.value = new Set(selectedSeats.value);
      myReservedSeats.value = new Set(myReservedSeats.value);
      reservedSeatIds.value = new Set(reservedSeatIds.value);

      // Publish optimistic event for other clients
      publishSeatIntent(seat, 'reserved');

      // API call to backend
      console.log('🟢 Calling addSeatToCart API for:', seatId);
      const success = await addSeatToCart(seat);
      console.log('🟢 addSeatToCart API result:', success);

      // Rollback on error
      if (!success) {
        selectedSeats.value.delete(seatId);
        myReservedSeats.value.delete(seatId);
        reservedSeatIds.value.delete(seatId);
        selectedSeats.value = new Set(selectedSeats.value);
        myReservedSeats.value = new Set(myReservedSeats.value);
        reservedSeatIds.value = new Set(reservedSeatIds.value);
      }
    }
  }

  // Add seat to cart via API
  // Returns true on success, false on error
  async function addSeatToCart(seat) {
    try {
      // Get ticket type for this seat
      const ticketType = seat.ticketType || getTicketTypeForSeat(seat);
      if (!ticketType) {
        console.error('No ticket type found for seat:', seat);
        return false;
      }

      // Generate visual label based on seat type (same logic as selectedSeatsList)
      const seatLabel = getSeatDisplayLabel(seat);

      const metadata = {
        event_name: eventDetail.value?.name || props.event.name,
        ticket_type_name: ticketType.title || ticketType.name,
        price: ticketType.price,
        seat_id: seat.id,
        seat_label: seatLabel,
        row: seat.row,
        number: seat.number,
      };

      const requestBody = {
        eventId: props.event.id,
        ticketTypeId: ticketType.id,
        quantity: 1,
        metadata: metadata,
      };

      const response = await fetch('/api/v2/cart/add-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to add seat to cart:', error);
        return false;
      }

      const data = await response.json();
      if (data.cart_summary) {
        cartStore.setCart(data.cart_summary);
      }

      return true;
    } catch (error) {
      console.error('Error adding seat to cart:', error);
      return false;
    }
  }

  // Remove seat from cart via API
  // Returns true on success, false on error
  async function removeSeatFromCart(seat) {
    try {
      // Get ticket type for this seat
      const ticketType = seat.ticketType || getTicketTypeForSeat(seat);
      if (!ticketType) {
        console.error('No ticket type found for seat:', seat);
        return false;
      }

      // Generate visual label based on seat type (same logic as selectedSeatsList)
      const seatLabel = getSeatDisplayLabel(seat);

      const metadata = {
        event_name: eventDetail.value?.name || props.event.name,
        ticket_type_name: ticketType.title || ticketType.name,
        price: ticketType.price,
        seat_id: seat.id,
        seat_label: seatLabel,
        row: seat.row,
        number: seat.number,
      };

      const response = await fetch('/api/v2/cart/remove-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventId: props.event.id,
          ticketTypeId: ticketType.id,
          quantity: 1,
          metadata: metadata,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to remove seat from cart:', error);
        return false;
      }

      const data = await response.json();
      if (data.cart_summary) {
        cartStore.setCart(data.cart_summary);
      }

      return true;
    } catch (error) {
      console.error('Error removing seat from cart:', error);
      return false;
    }
  }

  // Publish optimistic seat intent event for instant feedback to other clients
  // This is published BEFORE the API call completes for better UX
  function publishSeatIntent(seat, action) {
    const payload = buildSeatIntentPayload({
      eventId: props.event.id,
      seat,
      sessionUserId: props.sessionUserId,
      action,
    });

    try {
      if (!publishSeatIntentMessage(props.channel, payload)) {
        console.warn(
          'Cannot publish seat.intent: Ably channel not ready yet. Skipping optimistic update.'
        );
        // The backend event will still update other clients (100-300ms instead of 0-50ms)
        return;
      }

      console.log(
        `✓ Published seat.intent (${action}):`,
        payload.seatId,
        payload.seatLabel
      );
    } catch (error) {
      console.error('Failed to publish seat intent:', error);
    }
  }

  // Remove all selected seats from cart
  async function clearSelection() {
    // Remove each selected seat from cart
    const seatsToRemove = Array.from(selectedSeats.value);

    for (const seatId of seatsToRemove) {
      const seat = findSeatById(seatId);
      if (seat) {
        publishSeatIntent(seat, 'released');
        await removeSeatFromCart(seat);
      }
    }

    selectedSeats.value.clear();
    selectedSeats.value = new Set();
  }

  // Seats are already added to cart via handleSeatClick
  // This button just closes the modal and shows the cart
  function handleConfirm() {
    // Clear local selection state (seats are already in cart)
    selectedSeats.value.clear();
    selectedSeats.value = new Set();

    // Close modal - parent will show cart
    emit('close');
  }

  async function handleClose() {
    // When canceling, remove all selected seats from cart
    await clearSelection();
    emit('close');
  }

  // Ably handlers - Listen to backend-published events
  function handleTicketReserved(message) {
    const data = message.data;

    // Only handle events for this event
    if (data.eventId !== props.event.id) return;

    // If seat info is included, update immediately for better UX
    if (data.seatId) {
      // Add seat to reserved set immediately
      reservedSeatIds.value.add(data.seatId);
      reservedSeatIds.value = new Set(reservedSeatIds.value);
    }

    // Also refresh all reserved seats to ensure consistency
    fetchReservedSeats();
  }

  function handleTicketReleased(message) {
    const data = message.data;

    // Only handle events for this event
    if (data.eventId !== props.event.id) return;

    // If seat info is included, update immediately for better UX
    if (data.seatId) {
      // Remove seat from reserved set immediately
      reservedSeatIds.value.delete(data.seatId);
      myReservedSeats.value.delete(data.seatId);
      reservedSeatIds.value = new Set(reservedSeatIds.value);
      myReservedSeats.value = new Set(myReservedSeats.value);
    }

    // Also refresh all reserved seats to ensure consistency
    fetchReservedSeats();
  }

  function handleAvailabilityUpdate(message) {
    const data = message.data;

    // Refresh reserved seats when availability changes
    if (data.eventId === props.event.id) {
      fetchReservedSeats();
    }
  }

  // Handle optimistic seat intent events from other clients
  function handleSeatIntent(message) {
    const data = message.data;

    console.log('Received seat.intent event:', data);

    // Only handle events for this event
    if (data.eventId !== props.event.id) {
      console.log(
        'Ignoring seat.intent: different event',
        data.eventId,
        'vs',
        props.event.id
      );
      return;
    }

    // Ignore our own events
    if (data.userId === props.sessionUserId) {
      console.log('Ignoring seat.intent: own event');
      return;
    }

    console.log(
      `✓ Processing seat.intent (${data.action}) from user ${data.userId}:`,
      data.seatId,
      data.seatLabel
    );

    // Apply optimistic update immediately
    if (data.action === 'reserved') {
      reservedSeatIds.value.add(data.seatId);
      reservedSeatIds.value = new Set(reservedSeatIds.value);
      console.log(
        '✓ Added to reservedSeatIds, total reserved:',
        reservedSeatIds.value.size
      );
    } else if (data.action === 'released') {
      reservedSeatIds.value.delete(data.seatId);
      reservedSeatIds.value = new Set(reservedSeatIds.value);
      console.log(
        '✓ Removed from reservedSeatIds, total reserved:',
        reservedSeatIds.value.size
      );
    }

    // Note: We don't call fetchReservedSeats() here because:
    // 1. The optimistic update is instant
    // 2. The backend will publish ticket.reserved/released which will trigger fetchReservedSeats()
    // 3. This prevents double API calls
  }

  // Lifecycle
  watch(
    () => props.open,
    async (isOpen) => {
      if (isOpen) {
        // First load event details to get ticket types
        await loadEventDetail();
        // Then load floor plan (which also fetches reserved seats)
        await loadFloorPlan();
      } else {
        clearSelection();
      }
    },
    { immediate: true }
  );

  // Note: Removed watcher for props.reservedSeats since we now fetch reserved seats via API
  // in fetchReservedSeats() which is called from loadFloorPlan()

  onMounted(() => {
    // Subscribe to Ably channels for backend-published events
    if (props.channel && props.channel.subscribe) {
      props.channel.subscribe('ticket.reserved', handleTicketReserved);
      props.channel.subscribe('ticket.released', handleTicketReleased);
      props.channel.subscribe('availability.update', handleAvailabilityUpdate);
      props.channel.subscribe('seat.intent', handleSeatIntent); // Optimistic updates
    }
  });

  onUnmounted(() => {
    // Unsubscribe from Ably channels
    if (props.channel && props.channel.unsubscribe) {
      props.channel.unsubscribe('ticket.reserved', handleTicketReserved);
      props.channel.unsubscribe('ticket.released', handleTicketReleased);
      props.channel.unsubscribe(
        'availability.update',
        handleAvailabilityUpdate
      );
      props.channel.unsubscribe('seat.intent', handleSeatIntent); // Optimistic updates
    }

    // Clear any selections (remove from cart)
    clearSelection();
  });
</script>

<template>
  <!-- ========================================================================== -->
  <!-- MOBILE VIEW - Step-based fullscreen experience -->
  <!-- ========================================================================== -->
  <Teleport v-if="isMobile && open" to="body">
    <div class="mobile-seat-selection">
      <!-- Mobile Step 1: Floor Plan Selection -->
      <div
        v-if="mobileStep === 'floorplan'"
        class="mobile-seat-selection__container"
      >
        <!-- Compact Header -->
        <div class="mobile-seat-selection__header">
          <button
            class="-ml-2 p-2 text-gray-500 hover:text-gray-700"
            @click="handleClose"
          >
            <i class="fas fa-arrow-left text-lg"></i>
          </button>
          <div class="flex-1 px-2 text-center">
            <h2 class="truncate text-base font-semibold text-gray-900">
              {{ eventDisplayName }}
            </h2>
            <p class="text-xs text-gray-500">{{ eventDisplayDate }}</p>
          </div>
          <div class="w-10"></div>
        </div>

        <!-- Floor plan - fills remaining space -->
        <div class="mobile-seat-selection__content">
          <div
            v-if="loading"
            class="absolute inset-0 flex items-center justify-center"
          >
            <div class="text-center">
              <i class="fas fa-spinner fa-spin text-primary mb-3 text-3xl"></i>
              <p class="text-sm text-gray-600">
                {{ t('tickets.loading_floor_plan') }}
              </p>
            </div>
          </div>

          <FloorPlanRenderer
            v-if="floorPlanConfig"
            mode="selection"
            :floorPlanConfig
            :ticketTypes
            :seatTicketMappings
            :selectedSeatsSet="selectedSeats"
            :reservedSeatsSet
            :disabledSeats="event.blockedSeatIds || []"
            enableZoom
            showFullLabels
            @seat-click="handleSeatClick"
          />
        </div>

        <!-- Bottom section -->
        <div class="mobile-seat-selection__footer">
          <!-- Compact Legend -->
          <div class="border-t border-gray-200 px-3 py-2">
            <div
              class="flex flex-wrap items-center justify-center gap-3 text-xs"
            >
              <div class="flex items-center gap-1">
                <div
                  class="h-4 w-4 rounded border border-green-700 bg-green-500"
                ></div>
                <span class="text-gray-600">
                  {{ t('tickets.seat_selected') }}
                </span>
              </div>
              <div class="flex items-center gap-1">
                <div
                  class="h-4 w-4 rounded border border-gray-600 bg-gray-400 opacity-50"
                ></div>
                <span class="text-gray-600">
                  {{ t('tickets.seat_reserved') }}
                </span>
              </div>
            </div>
          </div>

          <!-- Bottom Action Bar -->
          <div class="pb-safe border-t-2 border-gray-300 px-4 py-3">
            <button
              class="bg-primary-600 flex w-full items-center gap-3 rounded-xl px-4 py-3 font-semibold text-white shadow-lg"
              :class="selectedSeats.size === 0 ? 'opacity-50' : ''"
              @click="mobileStep = 'selection'"
            >
              <i class="far fa-chair text-lg"></i>
              <span class="flex-1 text-center">
                <template v-if="selectedSeats.size === 0">
                  {{ t('tickets.select_seats') }}
                </template>
                <template v-else>
                  {{ t('tickets.view_selection') }} ({{ selectedSeats.size }})
                  <span class="ml-2">€{{ totalPrice.toFixed(2) }}</span>
                </template>
              </span>
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile Step 2: Selection Overview -->
      <div
        v-else-if="mobileStep === 'selection'"
        class="mobile-seat-selection__container"
      >
        <!-- Header -->
        <div class="mobile-seat-selection__header">
          <button
            class="-ml-2 p-2 text-gray-500 hover:text-gray-700"
            @click="mobileStep = 'floorplan'"
          >
            <i class="fas fa-arrow-left text-lg"></i>
          </button>
          <div class="flex-1 text-center">
            <h2 class="text-base font-semibold text-gray-900">
              {{ t('tickets.selected_seats') }}
            </h2>
            <p class="text-xs text-gray-500">
              {{ selectedSeats.size }} {{ t('tickets.seats_selected_count') }}
            </p>
          </div>
          <button
            class="-mr-2 p-2 text-gray-500 hover:text-gray-700"
            @click="handleClose"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>

        <!-- Selected Seats List -->
        <div
          class="mobile-seat-selection__content overflow-y-auto bg-gray-50 px-4 py-4"
        >
          <div
            v-if="selectedSeatsList.length === 0"
            class="flex h-full flex-col items-center justify-center py-12 text-center"
          >
            <div
              class="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-200"
            >
              <i class="far fa-chair text-3xl text-gray-400"></i>
            </div>
            <p class="mb-2 font-medium text-gray-600">
              {{ t('tickets.no_seats_selected') }}
            </p>
            <p class="mb-6 text-sm text-gray-400">
              {{ t('tickets.click_seats_to_select') }}
            </p>
            <button
              class="text-primary font-medium"
              @click="mobileStep = 'floorplan'"
            >
              <i class="fas fa-arrow-left mr-2"></i>
              {{ t('tickets.back_to_floorplan') }}
            </button>
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="seat in selectedSeatsList"
              :key="seat.id"
              class="flex items-center justify-between rounded-xl bg-white p-4 shadow-sm"
            >
              <div class="flex items-center gap-4">
                <div
                  class="flex h-12 w-12 items-center justify-center rounded-lg text-white"
                  :style="{
                    backgroundColor: seat.ticketType?.color || '#3B82F6',
                  }"
                >
                  <i class="far fa-chair text-xl"></i>
                </div>
                <div>
                  <p class="font-semibold text-gray-900">{{ seat.label }}</p>
                  <p class="text-sm text-gray-500">
                    {{ seat.ticketType?.title }}
                  </p>
                </div>
              </div>
              <p class="font-bold text-gray-900">
                €{{ seat.price.toFixed(2) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Bottom Actions -->
        <div
          class="mobile-seat-selection__footer pb-safe space-y-2 border-t-2 border-gray-300 px-4 py-3"
        >
          <!-- Total -->
          <div class="flex items-center justify-between text-lg">
            <span class="text-gray-600">{{ t('tickets.total') }}</span>
            <span class="font-bold text-gray-900">
              €{{ totalPrice.toFixed(2) }}
            </span>
          </div>

          <!-- Show Cart Button -->
          <button
            :disabled="selectedSeats.size === 0"
            class="bg-primary-600 flex w-full items-center justify-center gap-2 rounded-xl py-3 font-semibold text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
            @click="handleConfirm"
          >
            <i class="fa-solid fa-cart-shopping"></i>
            {{ t('tickets.show_cart') }} ({{ selectedSeats.size }})
          </button>

          <!-- Clear Button -->
          <button
            v-if="selectedSeats.size > 0"
            class="w-full py-1 font-medium text-gray-500"
            @click="clearSelection"
          >
            <i class="far fa-times mr-2"></i>
            {{ t('tickets.clear_selection') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ========================================================================== -->
  <!-- DESKTOP VIEW - Side-by-side layout -->
  <!-- ========================================================================== -->
  <BaseModal v-if="!isMobile" :open noPadding size="full" @close="handleClose">
    <div class="flex h-[calc(100vh-8rem)]">
      <!-- Main Floor Plan Area -->
      <div class="flex flex-1 flex-col overflow-hidden bg-gray-50">
        <!-- Event Info Header -->
        <div class="border-b-2 border-gray-300 bg-white px-6 py-5">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="mb-2 text-xl font-bold text-gray-900">
                {{ eventDisplayName }}
              </h2>
              <p class="text-sm text-gray-600">
                <i class="far fa-calendar mr-2"></i>
                {{ eventDisplayDate }}
                <i class="far fa-clock ml-4 mr-2"></i>
                {{ eventDisplayStartTime }} - {{ eventDisplayEndTime }}
                <span v-if="eventDisplayLocation" class="ml-4">
                  <i class="far fa-map-marker-alt mr-2"></i>
                  {{ eventDisplayLocation }}
                </span>
              </p>
            </div>
          </div>
        </div>

        <!-- Floor plan -->
        <div class="relative flex-1 overflow-hidden">
          <div
            v-if="loading"
            class="absolute inset-0 flex items-center justify-center bg-gray-50"
          >
            <div class="text-center">
              <i class="fas fa-spinner fa-spin text-primary mb-4 text-4xl"></i>
              <p class="text-gray-600">{{ t('tickets.loading_floor_plan') }}</p>
            </div>
          </div>

          <FloorPlanRenderer
            v-if="floorPlanConfig"
            mode="selection"
            :floorPlanConfig
            :ticketTypes
            :seatTicketMappings
            :selectedSeatsSet="selectedSeats"
            :reservedSeatsSet
            :disabledSeats="event.blockedSeatIds || []"
            enableZoom
            showFullLabels
            @seat-click="handleSeatClick"
          />
        </div>

        <!-- Legend -->
        <div class="border-t-2 border-gray-300 bg-white px-6 py-4">
          <div class="flex flex-wrap items-center justify-center gap-6 text-sm">
            <div class="flex items-center gap-2">
              <div
                class="h-6 w-6 rounded border-2 border-green-700 bg-green-500"
              ></div>
              <span class="text-gray-700">
                {{ t('tickets.seat_selected') }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <div
                class="h-6 w-6 rounded border-2 border-gray-600 bg-gray-400 opacity-50"
              ></div>
              <span class="text-gray-700">
                {{ t('tickets.seat_reserved') }}
              </span>
            </div>
            <div
              v-for="ticketType in visibleTicketTypes"
              :key="ticketType.id"
              class="flex items-center gap-2"
            >
              <div
                class="h-6 w-6 rounded border-2"
                :style="{
                  backgroundColor: ticketType.color,
                  borderColor: darkenColorForBorder(ticketType.color),
                }"
              ></div>
              <span class="text-gray-700">
                {{ ticketType.title }} (€{{ ticketType.price.toFixed(2) }})
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop Sidebar -->
      <div class="flex w-96 flex-col border-l-2 border-gray-300 bg-white">
        <!-- Selected Seats Header -->
        <div class="border-b-2 border-gray-300 px-6 py-5">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ t('tickets.selected_seats') }}
            <span v-if="selectedSeats.size > 0" class="text-primary">
              ({{ selectedSeats.size }})
            </span>
          </h3>
        </div>

        <!-- Selected Seats List -->
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <div v-if="selectedSeatsList.length === 0" class="py-12 text-center">
            <i class="far fa-chair mb-4 text-4xl text-gray-300"></i>
            <p class="text-gray-500">{{ t('tickets.no_seats_selected') }}</p>
            <p class="mt-2 text-sm text-gray-400">
              {{ t('tickets.click_seats_to_select') }}
            </p>
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="seat in selectedSeatsList"
              :key="seat.id"
              class="flex items-center justify-between rounded-lg bg-gray-50 p-3"
            >
              <div class="flex items-center gap-3">
                <div
                  class="flex h-10 w-10 items-center justify-center rounded font-semibold text-white"
                  :style="{
                    backgroundColor: seat.ticketType?.color || '#3B82F6',
                  }"
                >
                  <i class="far fa-chair"></i>
                </div>
                <div>
                  <p class="font-medium text-gray-900">{{ seat.label }}</p>
                  <p class="text-sm text-gray-600">
                    {{ seat.ticketType?.title }}
                  </p>
                </div>
              </div>
              <div class="text-right">
                <p class="font-semibold text-gray-900">
                  €{{ seat.price.toFixed(2) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer with Total and Actions -->
        <div class="mt-auto space-y-4 border-t-2 border-gray-300 px-6 py-5">
          <!-- Total -->
          <div class="flex items-center justify-between text-lg font-semibold">
            <span class="text-gray-900">{{ t('tickets.total') }}</span>
            <span class="text-primary">€{{ totalPrice.toFixed(2) }}</span>
          </div>

          <!-- Actions -->
          <div class="space-y-2">
            <BaseButton
              variant="primary"
              size="lg"
              class="w-full"
              :disabled="selectedSeats.size === 0"
              @click="handleConfirm"
            >
              <i class="fa-solid fa-cart-shopping mr-2"></i>
              {{ t('tickets.show_cart') }} ({{ selectedSeats.size }})
            </BaseButton>

            <BaseButton
              variant="secondary"
              size="lg"
              class="w-full"
              :disabled="selectedSeats.size === 0"
              @click="clearSelection"
            >
              <i class="far fa-times mr-2"></i>
              {{ t('tickets.clear_selection') }}
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
